/**
 * Generic PrimeNG CSS custom property bridge resolver.
 *
 * Used by both browser-side diagnostics and CI-side AI context building
 * to trace which component-scoped CSS variables control which PrimeNG
 * tokens on child elements (checkboxes, radios, toggles, sliders, icons, etc.).
 *
 * The component map's `primengBridge` field provides the mapping:
 *   { "--my-component-var": { primengToken: "--p-checkbox-checked-background" } }
 *
 * This module resolves those mappings in reverse: given ancestor bridge entries
 * and child computed token values, it tells you which component variable
 * controls the visible color of which PrimeNG child element.
 */

/** CSS selectors covering common PrimeNG interactive child elements. */
export const PRIMENG_CHILD_SELECTORS = [
  '.p-checkbox-icon', '[data-pc-name="checkboxicon"]', '.p-checkbox-box',
  '.p-radiobutton-icon', '.p-radiobutton-box',
  '.p-inputswitch-slider', '.p-inputswitch-handle',
  '.p-togglebutton-icon', '.p-selectbutton-icon',
  '.p-slider-handle', '.p-slider-range',
  '.p-inputicon', '.p-icon', '[class*="p-icon"]',
  '.pi-check', '.pi-times', '.pi-search', '.pi-chevron-down',
  '[class*="pi-"]', 'svg[class*="check"]', 'svg[class*="icon"]',
  '.p-multiselect-check-icon', '.p-multiselect-close-icon',
  '.p-dropdown-trigger-icon', '.p-autocomplete-dropdown-icon',
  'i.material-icons', 'i.fa', 'i.fas', 'i.far',
  'i', 'svg'
];

/** CSS properties to collect from child elements for color/visibility diagnostics. */
export const PRIMENG_COLOR_PROPERTIES: ReadonlyArray<string> = [
  'color', 'background-color', 'fill', 'stroke'
];

/** Void HTML elements that can never contain children. */
export const VOID_ELEMENT_TAGS: ReadonlyArray<string> = [
  'INPUT', 'IMG', 'BR', 'HR', 'META', 'LINK',
  'SOURCE', 'TRACK', 'WBR', 'AREA', 'BASE', 'COL', 'EMBED'
];

/**
 * Returns the best element to search for PrimeNG child elements.
 * Void elements (input, img, etc.) cannot contain children, so walk up
 * to the nearest parent that can. Capped at 3 levels to avoid runaway walks.
 */
export function getSearchContainerForChildren(element: Element | null): Element | null {
  if (!element) return null;
  let current: Element | null = element;
  for (let depth = 0; depth < 3 && current; depth++) {
    if (current.children.length > 0) return current;
    if (depth === 0 && !VOID_ELEMENT_TAGS.includes(current.tagName)) return current;
    current = current.parentElement;
  }
  return element;
}

/** Common PrimeNG CSS custom property state suffixes. */
export const PRIMENG_STATES = [
  '', 'checked', 'unchecked', 'hover', 'checked-hover',
  'focus', 'selected', 'disabled', 'filled', 'invalid', 'active'
];

/** Common PrimeNG CSS custom property suffixes. */
export const PRIMENG_TOKEN_PROPERTIES = [
  'background', 'color', 'border-color', 'background-color', 'icon-color'
];

/**
 * Derive all plausible PrimeNG CSS custom property tokens for a base component name.
 * No per-component hardcoding — works for any p-* component.
 *
 * Generates two naming schemes:
 * 1. `--p-{base}-{state}-{prop}` (e.g. `--p-checkbox-checked-background`)
 * 2. `--p-{base}-icon-{state}-color` (e.g. `--p-checkbox-icon-checked-color`)
 *    PrimeNG uses this icon-first ordering for icon colour tokens.
 *
 * Example: enumeratePlausibleTokens('checkbox') →
 *   ['--p-checkbox-background', '--p-checkbox-color', '--p-checkbox-checked-background',
 *    '--p-checkbox-icon-checked-color', ...]
 */
export function enumeratePlausibleTokens(baseName: string): string[] {
  const tokens: string[] = [];
  for (const state of PRIMENG_STATES) {
    for (const prop of PRIMENG_TOKEN_PROPERTIES) {
      const infix = state ? `-${state}` : '';
      tokens.push(`--p-${baseName}${infix}-${prop}`);
    }
    // Icon-first naming: --p-{base}-icon-{state}-color (PrimeNG convention)
    if (state) {
      tokens.push(`--p-${baseName}-icon-${state}-color`);
    }
  }
  return tokens;
}

/**
 * From a component chain, extract all p-* component base names and return
 * the union of plausible PrimeNG custom property tokens to inspect on child elements.
 */
export function enumerateTokensFromChain(componentChain: string[]): string[] {
  const tokens = new Set<string>();
  for (const name of componentChain) {
    if (!name.startsWith('p-')) continue;
    const base = name.replace(/^p-/, '');
    for (const token of enumeratePlausibleTokens(base)) {
      tokens.add(token);
    }
  }
  return [...tokens];
}

/** A single ancestor → PrimeNG token mapping from the component map. */
export interface PrimengBridgeEntry {
  /** The component-scoped CSS variable (e.g. --filtersMultiselectboxButtonActiveBackground). */
  componentVar: string;
  /** The PrimeNG theme token it maps to (e.g. --p-checkbox-checked-background). */
  primengToken: string;
  /** The scope/selector of the component that sets this variable. */
  scope: string;
}

/** A resolved mapping with values from both the ancestor and the child. */
export interface ResolvedBridgeMapping {
  primengToken: string;
  /** The computed value of the PrimeNG token on the child element. */
  tokenValue: string;
  componentVar: string;
  /** The computed value of the component variable on the ancestor host. */
  componentVarValue: string;
  scope: string;
}

/**
 * Match ancestor primengBridge entries against child computed token values.
 * Only returns mappings where a child token has a non-empty value.
 *
 * @param ancestorBridges - Bridge entries collected from ancestor component hosts.
 * @param childTokenValues - A Map of PrimeNG token → computed value, read from the child.
 */
export function resolveBridgeMappings(
  ancestorBridges: PrimengBridgeEntry[],
  childTokenValues: Map<string, string>
): ResolvedBridgeMapping[] {
  const mappings: ResolvedBridgeMapping[] = [];
  for (const { componentVar, primengToken, scope } of ancestorBridges) {
    const tokenValue = childTokenValues.get(primengToken);
    if (!tokenValue) continue;
    mappings.push({
      primengToken,
      tokenValue,
      componentVar,
      componentVarValue: '',
      scope
    });
  }
  return mappings;
}

/**
 * Extract all primengBridge entries from a list of component map candidates
 * into a flat array suitable for ai-context hints or bridge-resolution input.
 */
export function extractPrimengBridgeHints(
  componentCandidates: Array<{
    selector?: string;
    primengBridge?: Record<string, { primengToken: string }>;
    scope?: string;
  }>
): PrimengBridgeEntry[] {
  const result: PrimengBridgeEntry[] = [];
  for (const candidate of componentCandidates) {
    const bridge = candidate.primengBridge;
    const scope = candidate.scope || candidate.selector || 'unknown';
    if (!bridge) continue;
    for (const [componentVar, mapping] of Object.entries(bridge)) {
      result.push({
        componentVar,
        primengToken: mapping.primengToken,
        scope
      });
    }
  }
  return result;
}

/** PrimeNG component class prefix patterns for type inference. */
const PRIMENG_COMPONENT_CLASSES = [
  { pattern: /^p-(checkbox|radiobutton|inputswitch|togglebutton|selectbutton|slider|multiselect|dropdown|autocomplete)/, component: 1 }
];

/**
 * Infer the PrimeNG base component type from a child element's CSS classes.
 *
 * When PrimeNG renders internal components (like checkboxes inside a
 * multiselect item) as regular `<div>` elements with class names rather
 * than custom elements, this function extracts the base component name
 * from the child's class list (e.g. `p-checkbox-icon` → `checkbox`).
 *
 * Falls back to checking the parent element's classes if the child
 * itself doesn't yield a match.
 *
 * @returns The base component name (e.g. `'checkbox'`, `'radiobutton'`)
 *          or an empty string if no known PrimeNG class is found.
 */
export function inferPrimeNgComponentType(element: HTMLElement): string {
  for (const cls of Array.from(element.classList)) {
    for (const { pattern, component } of PRIMENG_COMPONENT_CLASSES) {
      const match = cls.match(pattern);
      if (match && match[component]) return match[component];
    }
  }
  const parent = element.parentElement;
  if (parent) {
    for (const cls of Array.from(parent.classList)) {
      for (const { pattern, component } of PRIMENG_COMPONENT_CLASSES) {
        const match = cls.match(pattern);
        if (match && match[component]) return match[component];
      }
    }
  }
  return '';
}

/**
 * Search a list of CSS custom property hints (from `existingStyleVariableHints`)
 * against a child element's computed style to find the variable that controls
 * its color.
 *
 * Priority order:
 * 1. Hint whose computed value equals `targetColor` — this variable IS
 *    responsible for the broken appearance. Override this to fix it.
 * 2. Fallback: first color-related hint with any non-empty computed value.
 *
 * Used by the proposal engine when dynamic PrimeNG token discovery doesn't
 * yield a candidate. The hints come from `classifyFixRouting()` which extracts
 * all `--variable` names from the app's transitive SCSS files at build time.
 *
 * @param variableHints  CSS variable names from `existingStyleVariableHints`.
 * @param element        The child element to inspect.
 * @param targetColor    The broken colour to match against (e.g. `'white'`).
 * @returns The best-matching variable name, or `null`.
 */
export function resolveVariableFromHints(
  variableHints: string[],
  element: HTMLElement,
  targetColor: string
): string | null {
  if (!variableHints.length || !element || !targetColor) return null;

  let fallback: string | null = null;

  try {
    const style = getComputedStyle(element);

    for (const hint of variableHints) {
      const val = style.getPropertyValue(hint).trim();
      if (!val || val === 'transparent' || val.startsWith('rgba(0, 0, 0, 0)')) continue;

      if (!fallback) fallback = hint;

      if (val.toLowerCase() === targetColor.toLowerCase()) return hint;

      const normalizedVal = normalizeColorForComparison(val);
      const normalizedTarget = normalizeColorForComparison(targetColor);
      if (normalizedVal && normalizedTarget && normalizedVal === normalizedTarget) return hint;
    }
  } catch {
    return null;
  }

  return fallback;
}

/**
 * Normalise a CSS colour value to a canonical hex string for comparison.
 * Handles `rgb(...)`, `rgba(...)`, and named colours via canvas rendering.
 * Returns `null` if the colour cannot be normalised.
 */
function normalizeColorForComparison(color: string): string | null {
  if (!color) return null;

  // Already a hex
  const hexMatch = color.match(/^#([0-9a-f]{3,8})$/i);
  if (hexMatch) {
    const hex = hexMatch[1];
    if (hex.length === 3) {
      return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`.toLowerCase();
    }
    if (hex.length === 6) return `#${hex}`.toLowerCase();
    if (hex.length === 8) return `#${hex.slice(0, 6)}`.toLowerCase();
    return null;
  }

  // rgb / rgba
  const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  // Named colours — use a temporary canvas element to parse
  try {
    const ctx = (document.createElement('canvas').getContext('2d') as CanvasRenderingContext2D);
    if (!ctx) return null;
    ctx.fillStyle = color;
    const computed = ctx.fillStyle;
    if (!computed || computed === color) return null; // couldn't parse
    return normalizeColorForComparison(computed); // recurse with the rgb() result
  } catch {
    return null;
  }
}
